import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import costSharingAmountApi, {
  CostSharingAmountValues,
  DeductableAmountValue,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/costSharingAmount'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import FormikDefaultInputField from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'
import Icon from 'common/components/general/Icon'

import { commandRequest } from 'common/RequestUtils'

import { BaseComponentProps } from '../../../../common/productClassWizardModal/Common'

import './ProductClassStyles.less'

const deductibleAmountValidatorByFamilyStatus = (translate: (key: string) => string, offerSameAllowance: Boolean) => {
  return Yup.number()
    .nullable()
    .notRequired()
    .when('deductable', {
      is: (deductable: Boolean) => !offerSameAllowance && deductable,
      then: Yup.number()
        .nullable()
        .positive(translate('admin-dashboard.benefit-details.class-design.errors.positive-number'))
        .typeError(translate('admin-dashboard.benefit-details.class-design.errors.enter-amount'))
        .required(translate('general.required')),
    })
}

const formValidator = (translate: (key: string) => string, offerSameAllowance: Boolean) =>
  Yup.object({
    deductable: Yup.boolean().nullable().required(translate('general.required')),

    sameDeductableAmount: Yup.number()
      .nullable()
      .notRequired()
      .when('deductable', {
        is: (deductable: Boolean) => deductable && offerSameAllowance,
        then: Yup.number()
          .nullable()
          .positive(translate('admin-dashboard.benefit-details.class-design.errors.positive-number'))
          .typeError(translate('admin-dashboard.benefit-details.class-design.errors.enter-amount'))
          .required(translate('general.required')),
      }),

    singleAmount: deductibleAmountValidatorByFamilyStatus(translate, offerSameAllowance),
    singleWithDependentsAmount: deductibleAmountValidatorByFamilyStatus(translate, offerSameAllowance),
    marriedAmount: deductibleAmountValidatorByFamilyStatus(translate, offerSameAllowance),
    marriedWithDependentsAmount: deductibleAmountValidatorByFamilyStatus(translate, offerSameAllowance),
  })

export interface CostSharingAmountProps {
  productClassId: string
  offerSameAllowance: boolean

  deductable: boolean | null
  sameDeductableAmount: number | null

  singleAmount: number | null
  singleWithDependentsAmount: number | null
  marriedAmount: number | null
  marriedWithDependentsAmount: number | null
  planPeriodId?: string
}

const CostSharingAmount: React.FC<BaseComponentProps<CostSharingAmountProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [deductable, setDeductable] = useState<boolean | null>(props.state.deductable)
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()

  useEffect(() => {
    props.setIsFormValid(formValidator(translate, props.state.offerSameAllowance).isValidSync(props.state))
  }, [])

  const onSubmit = async (values: CostSharingAmountProps) => {
    values.productClassId = props.state.productClassId

    var deductableValues: DeductableAmountValue[] = []

    if (values.deductable) {
      deductableValues.push({
        familyStatusId: FamilyStatus.Single,
        amount: Number(props.state.offerSameAllowance ? values.sameDeductableAmount ?? 0 : values.singleAmount ?? 0),
      })

      deductableValues.push({
        familyStatusId: FamilyStatus.SingleWithDependents,
        amount: Number(
          props.state.offerSameAllowance ? values.sameDeductableAmount ?? 0 : values.singleWithDependentsAmount ?? 0,
        ),
      })

      deductableValues.push({
        familyStatusId: FamilyStatus.Married,
        amount: Number(props.state.offerSameAllowance ? values.sameDeductableAmount ?? 0 : values.marriedAmount ?? 0),
      })

      deductableValues.push({
        familyStatusId: FamilyStatus.MarriedWithDependents,
        amount: Number(
          props.state.offerSameAllowance ? values.sameDeductableAmount ?? 0 : values.marriedWithDependentsAmount ?? 0,
        ),
      })
    }

    const deductableObject: CostSharingAmountValues = {
      productClassId: props.state.productClassId,
      deductable: values.deductable,
      deductibleAmount: deductableValues,
      planPeriodId: planPeriod,
    }

    const response = await commandRequest(() => costSharingAmountApi(deductableObject))
    if (response.errors) {
      props.setIsSubmiting(false)
      notification.open({
        message: response.errors[0],
        type: 'error',
      })
    } else {
      if (props.editing) {
        props.save()
      } else {
        props.goNext()
      }
    }
  }

  const onChange = (e: any, formik: FormikProps<CostSharingAmountProps>) => {
    if (!e.target.value) {
      formik.setFieldValue('sameDeductableAmount', 0)
      formik.setFieldValue('singleAmount', 0)
      formik.setFieldValue('singleWithDependentsAmount', 0)
      formik.setFieldValue('marriedAmount', 0)
      formik.setFieldValue('marriedWithDependentsAmount', 0)
    } else {
      formik.setFieldValue('sameDeductableAmount', null)
      formik.setFieldValue('singleAmount', null)
      formik.setFieldValue('singleWithDependentsAmount', null)
      formik.setFieldValue('marriedAmount', null)
      formik.setFieldValue('marriedWithDependentsAmount', null)
    }
    setDeductable(e.target.value)
    formik && formik.setFieldValue('deductable', e.target.value)
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate, props.state.offerSameAllowance)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate, props.state.offerSameAllowance).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        // @ts-ignore
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="cost_sharing_amount">
            <Row>
              <Col span={22}>
                <h5 className="cost_sharing_amount_info pl-8">
                  {translate('admin-dashboard.benefit-details.class-design.hra-deductible.header')}
                </h5>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={22}>
                <Radio.Group
                  name="deductable"
                  defaultValue={props.state.deductable}
                  onRadioChange={(e) => onChange(e, formik)}
                >
                  <Row gutter={10}>
                    <Col span={12}>
                      <Radio.Button className="large radio-with-checkmark cost_sharing_amount__radio" value={false}>
                        <div className="pl-16 pr-24 content">
                          <h5 className="option_title">
                            {translate('admin-dashboard.benefit-details.class-design.hra-deductible.no-deductible')}
                          </h5>
                        </div>
                      </Radio.Button>
                    </Col>
                    <Col span={12}>
                      <Radio.Button className="large radio-with-checkmark cost_sharing_amount__radio" value={true}>
                        <div className="pl-16 pr-24 content">
                          <h5 className="option_title">
                            {translate('admin-dashboard.benefit-details.class-design.hra-deductible.yes-deductible')}
                          </h5>
                        </div>
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>

            {deductable && (
              <>
                <Row>
                  <Col span={22} className="p-8">
                    <hr />
                  </Col>
                </Row>
                <Row className="pt-8">
                  <Col span={22} className="pl-8 pr-8">
                    <Alert
                      type="info"
                      showIcon
                      message={translate('admin-dashboard.benefit-details.class-design.hra-deductible.alert-header')}
                      description={translate('admin-dashboard.benefit-details.class-design.hra-deductible.alert-desc')}
                    />
                  </Col>
                </Row>
                <Row className="form__row">
                  <Col span={22}>
                    <h5 className="cost_sharing_amount_info pl-8 pr-8">
                      {translate('admin-dashboard.benefit-details.class-design.hra-deductible.class-deductible')}
                    </h5>
                  </Col>
                </Row>
              </>
            )}

            {deductable && props.state.offerSameAllowance && (
              <Row className="form__row pl-8">
                <Col span={11}>
                  <FormikDefaultInputField
                    label={translate(
                      'admin-dashboard.benefit-details.class-design.hra-deductible.deductible-amount-label',
                    )}
                    name="sameDeductableAmount"
                    placeholder="ex. 100.00"
                    prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                  />
                </Col>
              </Row>
            )}

            {deductable && !props.state.offerSameAllowance && (
              <>
                <Row>
                  <Col span={12} className="pl-8 pr-8">
                    <FormikDefaultInputField
                      label={translate('admin-dashboard.benefit-details.class-design.hra-deductible.single-label')}
                      name="singleAmount"
                      placeholder="ex. 300.00"
                      prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                    />
                  </Col>
                  <Col span={12} className="pl-8 pr-8">
                    <FormikDefaultInputField
                      label={translate('admin-dashboard.benefit-details.class-design.hra-deductible.single-dep-label')}
                      name="singleWithDependentsAmount"
                      placeholder="ex. 700.00"
                      prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={12} className="pl-8 pr-8">
                    <FormikDefaultInputField
                      label={translate('admin-dashboard.benefit-details.class-design.hra-deductible.married-label')}
                      name="marriedAmount"
                      placeholder="ex. 700.00"
                      prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                    />
                  </Col>
                  <Col span={12} className="pl-8 pr-8">
                    <FormikDefaultInputField
                      label={translate('admin-dashboard.benefit-details.class-design.hra-deductible.married-dep-label')}
                      name="marriedWithDependentsAmount"
                      placeholder="ex. 700.00"
                      prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                    />
                  </Col>
                </Row>
              </>
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export default CostSharingAmount
